import { useEffect, useState } from "react";
import { MapPinned, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

export function ScheduleStep({ form, set, jobCode }) {
  const [days, setDays] = useState(null);

  useEffect(() => {
    const params = { kind: form.kind, equipment_type: form.equipment_type || undefined, code: jobCode, ...(form.lat != null ? { lat: form.lat, lng: form.lng } : {}) };
    api.get("/public/availability", { params })
      .then((r) => {
        setDays(r.data.days);
        const day = r.data.days.find((d) => d.date === form.date);
        if (form.date && !day?.slots.some((s) => s.start === form.start && s.available))
          set((p) => ({ ...p, date: day ? p.date : "", start: "", end: "" }));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [form.lat, form.lng, form.kind, form.equipment_type]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!days) return <div className="flex items-center gap-2 text-slate-500" data-testid="availability-loading"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo disponibilità…</div>;
  if (!days.length) return <p className="rounded-xl border border-dashed p-6 text-sm text-slate-500" data-testid="availability-empty">Nessuna data prenotabile online per questo tipo di intervento. Contattaci direttamente.</p>;

  const selected = days.find((d) => d.date === form.date);
  const nearby = days.filter((d) => d.tech_nearby).length;
  return (
    <div className="space-y-6">
      {nearby > 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-[#61a229]/30 bg-[#a2ca6b]/15 p-4 text-sm text-[#2f5212]" data-testid="nearby-banner">
          <MapPinned className="h-5 w-5 text-[#61a229]" />
          Abbiamo già tecnici nella tua zona in {nearby} {nearby === 1 ? "giorno" : "giorni"}: li trovi per primi.
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="nearby-none">Scegli il giorno e la fascia oraria che preferisci.</p>
      )}
      <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="day-list">
        {days.map((d) => {
          const free = d.slots.filter((s) => s.available).length;
          return (
            <button key={d.date} type="button" disabled={!free} data-testid={`day-option-${d.date}`} data-active={form.date === d.date}
              onClick={() => set((p) => ({ ...p, date: d.date, start: "", end: "" }))}
              className="choice relative min-w-[112px] shrink-0 rounded-2xl border bg-white p-3.5 text-left disabled:opacity-40">
              {d.tech_nearby && <span className="absolute -top-2 left-3 rounded-full bg-[#61a229] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">In zona</span>}
              <span className="block text-xs uppercase tracking-wider text-slate-500">{fmtDate(d.date, { weekday: "short" })}</span>
              <span className="block font-display text-2xl font-bold text-slate-900">{fmtDate(d.date, { day: "2-digit" })}</span>
              <span className="block text-xs text-slate-500">{fmtDate(d.date, { month: "short" })} · {free} slot</span>
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="space-y-3 rise">
          <p className="text-sm font-semibold">Fasce orarie disponibili</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {selected.slots.map((s) => (
              <button key={s.start} type="button" disabled={!s.available} data-testid={`slot-option-${s.start}`}
                data-active={form.start === s.start}
                onClick={() => set((p) => ({ ...p, start: s.start, end: s.end }))}
                className="choice rounded-xl border bg-white px-3 py-3 font-mono text-sm disabled:line-through disabled:opacity-40">
                {s.start} – {s.end}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
